/**
 * Shared source-cache / SWR contracts.
 * Provider APIs remain source of truth; cache layers only accelerate reads.
 */
export const DEFAULT_SOURCE_CACHE_POLICIES = {
    jira: { softTtlMs: 60_000, hardTtlMs: 15 * 60_000, persistLocal: true },
    github: { softTtlMs: 60_000, hardTtlMs: 15 * 60_000, persistLocal: true },
    slack: { softTtlMs: 45_000, hardTtlMs: 30 * 60_000, persistLocal: true },
    'slack-conversations': {
        softTtlMs: 45_000,
        hardTtlMs: 30 * 60_000,
        persistLocal: true,
    },
    'unattended-inbox': {
        softTtlMs: 45_000,
        hardTtlMs: 30 * 60_000,
        persistLocal: true,
    },
    agents: { softTtlMs: 30_000, hardTtlMs: 24 * 60 * 60_000, persistLocal: true },
    gmail: { softTtlMs: 60_000, hardTtlMs: 24 * 60 * 60_000, persistLocal: true },
    workspace: {
        softTtlMs: 120_000,
        hardTtlMs: 24 * 60 * 60_000,
        persistLocal: true,
    },
    settings: {
        softTtlMs: 30_000,
        hardTtlMs: 60 * 60_000,
        persistLocal: false,
    },
};
export function policyForSource(source) {
    return DEFAULT_SOURCE_CACHE_POLICIES[source];
}
function parseIsoMs(iso) {
    if (!iso)
        return null;
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : null;
}
/** Soft-stale when now is past staleAt, or past fetchedAt + softTtl when staleAt missing. */
export function isSoftStale(meta, policy, nowMs = Date.now()) {
    if (!meta?.fetchedAt)
        return true;
    const staleAt = parseIsoMs(meta.staleAt);
    if (staleAt !== null)
        return nowMs >= staleAt;
    const fetchedAt = parseIsoMs(meta.fetchedAt);
    if (fetchedAt === null)
        return true;
    return nowMs >= fetchedAt + policy.softTtlMs;
}
/** Hard-expired when past expiresAt, or past fetchedAt + hardTtl when expiresAt missing. */
export function isHardExpired(meta, policy, nowMs = Date.now()) {
    if (!meta?.fetchedAt)
        return true;
    const expiresAt = parseIsoMs(meta.expiresAt);
    if (expiresAt !== null)
        return nowMs >= expiresAt;
    const fetchedAt = parseIsoMs(meta.fetchedAt);
    if (fetchedAt === null)
        return true;
    return nowMs >= fetchedAt + policy.hardTtlMs;
}
export function resolveFreshness(meta, policy, nowMs = Date.now()) {
    if (!meta?.fetchedAt || isHardExpired(meta, policy, nowMs))
        return 'miss';
    if (isSoftStale(meta, policy, nowMs))
        return 'stale';
    return 'fresh';
}
export function buildSourceCacheMeta(input) {
    const nowMs = input.nowMs ?? Date.now();
    const fetchedAt = input.fetchedAt ?? new Date(nowMs).toISOString();
    const fetchedMs = parseIsoMs(fetchedAt) ?? nowMs;
    const staleAt = new Date(fetchedMs + input.policy.softTtlMs).toISOString();
    const expiresAt = new Date(fetchedMs + input.policy.hardTtlMs).toISOString();
    const freshness = input.forceFreshness ??
        resolveFreshness({ fetchedAt, staleAt, expiresAt }, input.policy, nowMs);
    return {
        fetchedAt,
        staleAt,
        expiresAt,
        freshness,
        layer: input.layer,
        source: input.source,
    };
}
/** Merge incoming cache meta onto an envelope without dropping other fields. */
export function mergeEnvelopeCacheMeta(envelope, cache) {
    return { ...envelope, cache };
}
//# sourceMappingURL=sourceCache.js.map